import moment from 'moment-timezone';

import { DATE_MONTH_DAY_YEAR } from '../../../../constants';

const join = (items, fn) => (items?.length ? items.map(fn).filter(v => v).join('; ') : undefined);
const joinParts = parts => parts.filter(v => v).join(', ');

// Every key a stored item can carry is shown: a bulk update replaces these lists whole, so a value
// only the portal wrote (a billing note, a medication's links) must be visible when it goes away.
const insuranceText = i => joinParts([i.insuranceName, i.policyId, i.groupNumber, i.payerId, i.insuranceAndBillingNotes]);
const prescriberText = p => joinParts([
  `${p.firstName || ''} ${p.lastName || ''}`.trim(), p.npi, p.type, p.phone, p.fax, p.email,
  p.address, p.city, p.state, p.zip,
]);
const providerText = p => joinParts([
  [p.firstName, p.middleName, p.lastName].filter(v => v).join(' '), p.npi, p.specialtyName, p.organizationName,
  p.directAddress, p.faxNumber,
]);
// An ACCESS enrollment the proposals concern: track (or none yet) and status (a new one has none),
// whether someone already acted on it, the consents recorded on it and the referring provider.
// Booleans read as yes/no so a declined consent is visible.
const yesNo = v => (v === undefined || v === null ? undefined : v ? 'yes' : 'no');
const enrollmentText = e => joinParts([
  e.track || 'no program', e.enrollmentId ? e.status : 'new', e.actedOn ? 'in progress' : undefined,
  yesNo(e.consentToParticipate) !== undefined ? `participation consent: ${yesNo(e.consentToParticipate)}` : undefined,
  yesNo(e.careUpdateConsent) !== undefined ? `care-update consent: ${yesNo(e.careUpdateConsent)}` : undefined,
  e.referringProvider ? `referred by ${providerText(e.referringProvider)}` : undefined,
]);
const medicationText = m => joinParts([
  m.name || m.dispensedName || m.prescribedName, m.ndc, m.rxNumber,
  m.name && m.dispensedName !== m.name ? m.dispensedName : undefined, m.prescribedName, m.fillDate, m.sig, m.notes,
  m.conditions?.length ? m.conditions.join(' / ') : undefined,
  m.insurance?.length ? m.insurance.map(insuranceText).join(' / ') : undefined,
  m.prescriber ? prescriberText(m.prescriber) : undefined,
]);

// One definition per candidatePatient key (the backend returns before and after in this same
// shape), in display order. The formatter turns the snapshot value into a display string.
export const CANDIDATE_FIELDS = [
  ['mrn'],
  ['pharmacyPatientId'],
  ['medicareBeneficiaryId'],
  ['firstName'],
  ['lastName'],
  ['dateOfBirth', v => moment(v).format(DATE_MONTH_DAY_YEAR)],
  ['gender'],
  ['language'],
  ['timezone'],
  ['primaryPhoneNo'],
  ['textPhoneNo'],
  ['email'],
  ['homeAddress'],
  ['address', a => joinParts([a.street, a.street2, a.city, a.state, a.zip, a.country])],
  ['conditions', v => v.join(', ')],
  ['insurance', v => join(v, insuranceText)],
  ['centerDetails', v => join(v, c => joinParts([c.centerName, c.orderingProvider, c.clinicContact]))],
  ['transplantDetails', v => join(v, t => joinParts([t.organType, t.transplantDate]))],
  ['medications', v => join(v, medicationText)],
  ['athenaPatientId'],
  ['athenaPracticeId'],
  ['athenaDepartmentId'],
  ['primaryCareProvider', providerText],
  ['enrollments', v => join(v, enrollmentText)],
];

// candidateTracks are the ACCESS tracks of a candidate's enrollments (after side, falling back to
// before), e.g. "CKM, MSK".
export const candidateTracks = candidate => {
  const enrollments = candidate.after?.enrollments || candidate.before?.enrollments || [];
  return [...new Set(enrollments.map(e => e.track).filter(t => t))].join(', ');
};

const formatField = (format, value) => {
  if (value === undefined || value === null) return undefined;
  return format ? format(value) : String(value);
};

// bulkUploadErrorText is the display text of one row/candidate error or warning: the message the
// backend attached (for a rejected create/update it is the same text the single-patient
// notification shows), else the text for its id, falling back to the id like the portal's
// notifications fall back to a generic text.
//
// A source issue (source_no_match, …) names its source: "{source}" in the text is replaced with the
// source's label, "{fields}" with the labels of the fields it lists and "{count}" with its count.
// A reason about one ACCESS track (a patient can have several) names it: in the text, or before it.
export const bulkUploadErrorText = (error, texts = {}, sources = {}, fieldLabels = {}) => {
  if (error.message) return error.message;
  let text = texts[error.id];
  if (!text) return error.id;
  if (error.track && !text.includes('{track}')) text = `${error.track}: ${text}`;
  return text
    .replaceAll('{source}', sources[error.source] || error.source || '')
    .replaceAll('{fields}', (error.fields || []).map(f => fieldLabels[f] || f).join(', '))
    .replaceAll('{count}', error.count ?? '')
    .replaceAll('{track}', error.track ?? '')
    .replaceAll('{decision}', error.decision ?? '')
    .replaceAll('{qualifying_count}', error.qualifying_count ?? 0)
    .replaceAll('{enrollment_track}', error.enrollment_track ?? '')
    .replaceAll('{enrollment_status}', error.enrollment_status ?? '');
};

// RECORD_FIELDS are the fields a record found by a source is shown with in the picker.
// Who the record is comes first, then the identifiers that tell records apart, then contact data.
const RECORD_FIELDS = [
  'firstName', 'lastName', 'dateOfBirth', 'athenaPatientId', 'mrn', 'medicareBeneficiaryId', 'textPhoneNo',
  'primaryPhoneNo', 'email', 'address', 'conditions', 'primaryCareProvider', 'athenaDepartmentId',
];

// recordRows lists a record's fields that have a value, formatted like the comparison.
export const recordRows = (record, labels = {}) => RECORD_FIELDS
  .map(key => {
    const format = CANDIDATE_FIELDS.find(([k]) => k === key)?.[1];
    return { key, label: labels[key] || key, value: formatField(format, record?.[key]) };
  })
  .filter(row => row.value !== undefined && row.value !== '');

// EVIDENCE_FIELDS are the fields a source compares with the sheet, in display order.
export const EVIDENCE_FIELDS = ['firstName', 'lastName', 'dateOfBirth', 'mrn', 'medicareBeneficiaryId', 'email', 'phone', 'athenaPatientId'];

// choosableRecord: a person may pick the record (a contradicted or conflicting one never is).
export const choosableRecord = record => record.verdict === 'certain' || record.verdict === 'unresolved';

// patientName is the candidate's display name, from the after side, falling back to before.
export const patientName = candidate => {
  const p = candidate.after || candidate.before;
  return p ? `${p.firstName || ''} ${p.lastName || ''}`.trim() : '';
};

// comparisonRows builds one Field / Before / After row per field present on either side, and
// flags the fields the backend reported in changed_fields.
export const comparisonRows = (candidate, labels = {}) => {
  const changed = new Set(candidate.changed_fields || []);
  return CANDIDATE_FIELDS
    .map(([key, format]) => ({
      key,
      label: labels[key] || key,
      before: formatField(format, candidate.before?.[key]),
      after: formatField(format, candidate.after?.[key]),
      changed: changed.has(key),
    }))
    .filter(row => row.before !== undefined || row.after !== undefined);
};

// Reason levels, most important first (the API gives each reason one): error — the patient won't
// be added; warning — worth a look; note — information only.
export const REASON_LEVELS = ['error', 'warning', 'note'];

// reasonIcon is the shared table icon class of a level.
export const reasonIcon = level => ({ error: 'alert', warning: 'warning', note: 'notes' }[level] || 'alert');

// sortReasons orders reasons by level, keeping the API's order within a level.
export const sortReasons = reasons => [...(reasons || [])].sort(
  (a, b) => REASON_LEVELS.indexOf(a.level || 'error') - REASON_LEVELS.indexOf(b.level || 'error'),
);
